import { keepPreviousData } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Filter, TimePrecision } from '@playq/octopus-common';
import { SessionsSort, SessionsResponse } from '@playq/octopus2-auth';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { services2 } from '/api/services2';
import { toMs } from '/helpers';

import { queryUserSessionsKey } from './constants';

const emptyArray: [] = [];
const emptyFilterBy: {
  [key: string]: Filter;
} = {};

export const useQueryUserSessions = (
  iterator: OffsetLimit,
  sortBy: SessionsSort[] = emptyArray,
  filterBy: {
    [key: string]: Filter;
  } = emptyFilterBy,
  options?: IUseQueryOptionsExtended<SessionsResponse | undefined, GenericFailure | Error>
) => {
  const keys = queryUserSessionsKey.concat(iterator, sortBy, filterBy);

  const { mutate, refetch, ...res } = useEitherQuery(
    keys,
    () => services2.userSessionsService.queryUserSessions(iterator, sortBy, filterBy),
    {
      placeholderData: keepPreviousData,
      staleTime: toMs(TimePrecision.Minute, 5),
      ...options,
    }
  );

  const total = res.data?.total ?? 0;

  useQueryPrefetch({
    keys,
    enabled: options?.enablePrefetch,
    total,
    args: [iterator, sortBy, filterBy],
    serviceMethod: services2.userSessionsService.queryUserSessions.bind(services2.userSessionsService),
  });

  const mutateAfterLogOut = useCallback(
    (id: number) => {
      mutate((prevResponse) => {
        if (prevResponse === undefined) {
          refetch();
          return;
        }

        const newResponse = new SessionsResponse(prevResponse.serialize());
        newResponse.sessions.forEach((s) => {
          if (s.id === id) {
            s.expiresAt = new Date();
          }
        });

        return newResponse;
      });
    },
    [mutate, refetch]
  );

  return {
    ...res,
    sessions: res.data?.sessions || emptyArray,
    total,
    keys,
    refetch,
    mutateAfterLogOut,
  };
};
